import { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import {
  GridRowModesModel,
  GridRowModes,
  GridSelectionModel,
  GridRowId,
  GridFilterModel,
  GridSortModel,
  getGridStringOperators,
  GridActionsCellItem,
  GridColumns,
} from '@mui/x-data-grid-pro';
import { CustomDataTable } from 'components/DatatableComponent';
import { MainLayoutComponent } from 'components/LayoutComponent/SidebarLayout/MainLayoutComponent';
import { PaginationAndSortingParams } from 'types/api/Common/PaginationTypes';
import { DASHBOARD_VIEW_MODE } from 'constant/DashboardTypeConstant';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Tooltip from '@mui/material/Tooltip';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PATH_CONSTANT } from 'constant/PathConstant';
import {
  useGetListInsightView,
  useDuplicateInsightViewDefinition,
} from 'services/v1/SystemTenant/AROView/InsightViewDefinitionService';
import { InsightViewAttributeItem } from 'types/api/SystemTenant/AROView/InsightViewDefinitionTypes';
import { ModalConfirmationComponent } from 'components/ModalComponent/ModalConfirmationComponent';

const panelStyle = {
  transform: 'translate3d(704px, 140.667px, 0px) !important',

  '& .MuiDataGrid-panelFooter': {
    display: 'none',
  },
};

export default function InsightViewDefinition() {
  const { dashboardViewMode } = useSelector((state: RootState) => state.auth);
  const { t } = useTranslation();
  const [rowData, setRowData] = useState<InsightViewAttributeItem[]>([]);
  const navigate = useNavigate();
  const [selectedDataID, setSelectedDataID] = useState<GridRowId[]>([]);
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [filter, setFilter] = useState<PaginationAndSortingParams>({
    page: 0,
    size: 20,
    filterValue: '',
    sortType: 'asc',
    filterOperator: 'contains',
  });

  const [filterButtonElement, setFilterButtonElement] = useState<HTMLButtonElement | null>(null);

  const { data: insightViewDefinitionList, isLoading } = useGetListInsightView();
  const { mutate: duplicateInsightView, isLoading: isDuplicating } = useDuplicateInsightViewDefinition();

  const [duplicateTarget, setDuplicateTarget] = useState<InsightViewAttributeItem | null>(null);

  const handleEditClick = (id: GridRowId) => () => {
    navigate(PATH_CONSTANT.SYSTEM.VIEW.EDIT_INSIGHT_VIEW_DEFINITION.replace(':insightViewId', id.toString()));
  };

  const handleDuplicateClick = (row: InsightViewAttributeItem) => () => {
    setDuplicateTarget(row);
  };

  const handleCloseDuplicateModal = () => {
    if (isDuplicating) return;
    setDuplicateTarget(null);
  };

  const handleConfirmDuplicate = () => {
    if (!duplicateTarget) return;
    duplicateInsightView(duplicateTarget.id, {
      onSuccess: () => {
        toast.success('Duplicate Insight View Definition successfully!');
        setDuplicateTarget(null);
      },
    });
  };

  const isAnyEdit = useMemo(() => {
    return rowModesModel && Object.values(rowModesModel).some((item) => item.mode === GridRowModes.Edit);
  }, [rowModesModel]);

  const isBuilderViewOnly = dashboardViewMode === DASHBOARD_VIEW_MODE.VIEW_ONLY;

  const columns = useMemo<GridColumns>(() => {
    const cols: GridColumns = [
      {
        field: 'code',
        headerName: 'Code',
        width: 60,
        editable: true,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'name',
        headerName: 'Insight Name',
        width: 300,
        editable: true,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
      {
        field: 'cubeName',
        headerName: 'Cube',
        width: 300,
        editable: true,
        filterOperators: getGridStringOperators().filter((operator) => ['contains', 'equals'].includes(operator.value)),
      },
    ];

    if (isBuilderViewOnly) return cols;

    return [
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Actions',
        width: 80,
        cellClassName: 'actions',

        getActions: ({ id, row }) => {
          return [
            // @ts-ignore
            <GridActionsCellItem
              disabled={isAnyEdit}
              icon={
                <Tooltip title='Edit'>
                  <EditIcon />
                </Tooltip>
              }
              key={`edit-${id}`}
              label='Edit'
              onClick={handleEditClick(id)}
            />,
            // @ts-ignore
            <GridActionsCellItem
              disabled={isAnyEdit || isDuplicating}
              icon={
                <Tooltip title='Duplicate'>
                  <ContentCopyIcon />
                </Tooltip>
              }
              key={`duplicate-${id}`}
              label='Duplicate'
              onClick={handleDuplicateClick(row as InsightViewAttributeItem)}
            />,
          ];
        },
      },
      ...cols,
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBuilderViewOnly, isAnyEdit, isDuplicating]);

  useEffect(() => {
    if (insightViewDefinitionList?.data) {
      setRowData(insightViewDefinitionList?.data);
      setRowModesModel(
        insightViewDefinitionList?.data?.reduce((acc, item) => {
          return { ...acc, [item.id]: GridRowModes.View };
        }, {}),
      );
    }
  }, [insightViewDefinitionList]);

  const handleChangePage = (newPage: number) => {
    setFilter({ ...filter, page: newPage });
  };
  const handleChangePageSize = (newPageSize: number) => {
    setFilter({ ...filter, size: newPageSize });
  };

  const handleOnFilter = (params: GridFilterModel) => {
    let filterObj = {};
    if (params.items.length) {
      filterObj = {
        filterValue: params.items[0].value,
        filterOperator: params.items[0].operatorValue,
      };
    } else {
      filterObj = {
        filterValue: '',
        filterOperator: 'contains',
      };
    }
    setFilter({
      ...filter,
      ...filterObj,
    });
  };

  const handleOnAddButtonClick = () => {
    navigate(PATH_CONSTANT.SYSTEM.VIEW.CREATE_INSIGHT_VIEW_DEFINITION);
  };

  const handleOnSortModelChange = (model: GridSortModel) => {
    if (model.length === 0) {
      setFilter({ ...filter, sortType: 'asc' });
      return;
    }
    setFilter({ ...filter, sortType: model[0].sort });
  };
  return (
    <MainLayoutComponent
      pageTitle='Insight View Definition'
      breadcrumbs={[t('SIDEBAR_MENU.DASHBOARD'), 'Insight View']}
    >
      <CustomDataTable
        initialState={{
          pagination: {
            page: 0,
          },
        }}
        loading={isLoading}
        checkboxSelection={false}
        rows={rowData}
        columns={columns}
        rowCount={insightViewDefinitionList?.data?.length ?? 0}
        page={filter.page || 0}
        pageSize={filter.size}
        autoHeight
        getRowId={(row) => row.id}
        rowModesModel={rowModesModel}
        disableSelectionOnClick
        rowsPerPageOptions={[5, 10, 20]}
        getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        experimentalFeatures={{ newEditingApi: true }}
        onFilterModelChange={handleOnFilter}
        onSortModelChange={handleOnSortModelChange}
        onPageChange={handleChangePage}
        onPageSizeChange={handleChangePageSize}
        onSelectionModelChange={(selectedId: GridSelectionModel) => {
          setSelectedDataID(selectedId);
        }}
        paginationMode='client'
        sortingMode='client'
        filterMode='client'
        componentsProps={{
          panel: {
            sx: panelStyle,
            anchorEl: filterButtonElement,
          },
          toolbar: {
            setFilterButtonElement: setFilterButtonElement,
            disabled: isLoading || isBuilderViewOnly,
            disabledDeleteButton: selectedDataID.length === 0,

            config: {
              onInsertClick: handleOnAddButtonClick,
              deleteButtonText: 'Delete',
              fieldFocusOnInsert: '',
              insertButtonText: 'Add New Insight View',
              insertInitialObject: {
                layerName: '',
                isNew: true,
              },
            },
          },
        }}
      />
      <ModalConfirmationComponent
        visible={Boolean(duplicateTarget)}
        title='Duplicate Insight View'
        onClose={handleCloseDuplicateModal}
        onCancel={handleCloseDuplicateModal}
        onApprove={handleConfirmDuplicate}
        messageType='warning'
        labelButtonApprove='Duplicate'
        labelButtonCancel='Cancel'
        dangerouslySetInnerHTML={{
          __html: `Are you sure you want to duplicate <strong>${duplicateTarget?.name ?? ''}</strong>?`,
        }}
      />
    </MainLayoutComponent>
  );
}
